/**
 * Passport · Section A — Home & Energy
 *
 * Renders the SEAI-derived data behind the teaser, with FULL transparency on
 * the heating-cost calculation (BER kWh/m²/yr × m² × €/kWh) so the user can
 * see exactly where the number comes from and refit by their own fuel.
 *
 * Optional `propertyId` prop lets the fuel selector hit the same endpoint with
 * a `?fuel=` override so the user can compare "what if it were on a heat pump?".
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Sun, Zap, Wrench, Info, Flame, Droplet, Bolt, Thermometer, Trees } from 'lucide-react';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Market-aware money formatter. Falls back to en-IE / EUR for IE callers
// that don't pass a section envelope.
const makeFmt = (section = {}) => {
  const locale = section.locale || 'en-IE';
  const currency = section.currency || 'EUR';
  return (n) =>
    n == null ? '—'
      : new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
};
const sym = (section = {}) => section.currency_symbol || '€';

const BER_BG = {
  // IE BER bands
  A1: 'bg-emerald-700', A2: 'bg-emerald-600', A3: 'bg-emerald-500',
  B1: 'bg-lime-500',    B2: 'bg-lime-600',    B3: 'bg-lime-700',
  C1: 'bg-yellow-500',  C2: 'bg-yellow-600',  C3: 'bg-amber-600',
  D1: 'bg-orange-500',  D2: 'bg-orange-600',
  E1: 'bg-red-500',     E2: 'bg-red-600',
  F:  'bg-red-700',     G:  'bg-red-800',
  // UK EPC single-letter bands
  A:  'bg-emerald-700', B:  'bg-lime-600',
  C:  'bg-yellow-600',  D:  'bg-orange-500', E:  'bg-red-500',
};

const FUEL_ICON = {
  gas:         Flame,
  oil:         Droplet,
  electricity: Bolt,
  heat_pump:   Thermometer,
  solid:       Trees,
};

const HomeEnergySection = ({ section, propertyId }) => {
  const [data, setData]   = useState(section);
  const [loading, setLoading] = useState(false);
  const [compare, setCompare] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);

  // Market envelope is attached to the section payload by the backend
  // so the same component renders euro on IE and sterling on UK.
  const fmt = makeFmt(data);
  const $ = sym(data);
  const energyLabel = data.energy_label || 'BER';
  const sourceLabel = data.country_label === 'UK' ? 'MHCLG EPC Register' : 'SEAI Public Register';

  const d       = data.details || {};
  const heating = d.heating;        // breakdown object
  const fuels   = d.fuel_options || [];
  const selected = d.selected_fuel || 'gas';
  const ber     = d.ber || '—';
  const berBg   = BER_BG[ber] || 'bg-stone-500';
  const retro   = d.retrofit;
  const solar   = d.solar;
  const ev      = d.ev_charger;

  const switchFuel = useCallback(async (fk) => {
    if (fk === selected || !propertyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/listings/${propertyId}/passport-report?fuel=${fk}`);
      const next = (r.data?.sections || []).find((s) => s.id === 'home_energy');
      if (next) setData(next);
    } catch {
      /* noop — leave existing data in place */
    } finally {
      setLoading(false);
    }
  }, [propertyId, selected]);

  const loadCompare = useCallback(async () => {
    if (!propertyId) return;
    setCompareOpen((o) => !o);
    if (compare) return; // already fetched
    setCompareLoading(true);
    try {
      const r = await axios.get(`${API}/api/listings/${propertyId}/heating-compare`);
      setCompare(r.data);
    } catch {
      setCompare({ available: false });
    } finally {
      setCompareLoading(false);
    }
  }, [propertyId, compare]);

  return (
    <div className="space-y-4 pt-2">
      {/* BER strip */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`${berBg} text-white font-bold text-xl w-14 h-14 rounded-md flex items-center justify-center shadow-sm`}>
          {ber}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">{energyLabel} Rating</div>
          <div className="text-xs text-stone-700">
            {d.ber_verified
              ? <span className="text-emerald-700 font-medium">{sourceLabel} · verified</span>
              : <span className="text-stone-500">As stated by the seller</span>}
          </div>
          {d.size_m2 && <div className="text-[11px] text-stone-500 mt-1">Floor area · {d.size_m2} m²</div>}
        </div>
      </div>

      {/* Heating-cost transparency panel */}
      {heating ? (
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-heating-card">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-stone-900">Annual heating cost</span>
              <Badge variant="outline" className="text-[10px] py-0">{heating.fuel_label}</Badge>
            </div>
            <div className="text-[10px] text-stone-400">Updated {heating.last_updated}</div>
          </div>

          {/* The transparent equation */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-sm" data-testid="passport-heating-breakdown">
            <div className="text-stone-700">
              <div className="text-[10px] uppercase text-stone-500 font-semibold">kWh / m² / yr</div>
              <div className="font-mono font-semibold">{heating.kwh_per_m2}</div>
              <div className="text-[10px] text-stone-400">{energyLabel} band midpoint</div>
            </div>
            <div className="text-stone-400 text-center self-center hidden sm:block">×</div>
            <div className="text-stone-700">
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Floor area</div>
              <div className="font-mono font-semibold">{heating.size_m2} m²</div>
              <div className="text-[10px] text-stone-400">= {heating.annual_kwh.toLocaleString()} kWh/yr</div>
            </div>
            <div className="text-stone-400 text-center self-center hidden sm:block">×</div>
            <div className="text-stone-700">
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Effective {$}/kWh</div>
              <div className="font-mono font-semibold">{$}{heating.effective_unit_eur.toFixed(4)}</div>
              <div className="text-[10px] text-stone-400">
                {heating.effective_factor < 1
                  ? `raw ${$}${heating.raw_unit_eur} × ${heating.effective_factor.toFixed(2)} COP factor`
                  : `raw rate`}
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-stone-100 flex items-baseline justify-between">
            <div className="text-stone-600 text-sm">= Annual cost</div>
            <div className="text-2xl font-bold text-purple-700" data-testid="passport-heating-total">
              {fmt(heating.annual_cost_eur)}<span className="text-sm text-stone-400 font-normal">/yr</span>
            </div>
          </div>

          {/* Fuel selector */}
          <div className="mt-4 pt-3 border-t border-stone-100">
            <div className="text-[10px] uppercase text-stone-500 font-semibold mb-2 flex items-center gap-1">
              Refit for your actual heating fuel
              <Info className="h-3 w-3" />
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="passport-fuel-selector">
              {fuels.map((f) => {
                const Icon = FUEL_ICON[f.key] || Flame;
                const isOn = f.key === selected;
                return (
                  <button
                    key={f.key}
                    type="button"
                    disabled={loading}
                    onClick={() => switchFuel(f.key)}
                    data-testid={`passport-fuel-${f.key}`}
                    className={
                      `text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition ${
                        isOn
                          ? 'bg-purple-700 text-white border-purple-700'
                          : 'bg-white text-stone-700 border-stone-200 hover:border-purple-300'
                      } ${loading ? 'opacity-60 cursor-wait' : ''}`
                    }
                  >
                    <Icon className="h-3 w-3" />
                    {f.label.split(' (')[0]}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-stone-500 mt-2">
              <strong>Source:</strong> {heating.source}. {heating.note}
            </div>

            {/* Compare all fuels toggle */}
            <div className="mt-3 pt-3 border-t border-stone-100">
              <button
                type="button"
                onClick={loadCompare}
                data-testid="passport-fuel-compare-toggle"
                className="text-xs font-semibold text-purple-700 hover:underline"
              >
                {compareOpen ? '− Hide fuel comparison' : '+ Compare every fuel side-by-side'}
              </button>

              {compareOpen && (
                <div className="mt-3" data-testid="passport-fuel-compare-panel">
                  {compareLoading && (
                    <div className="text-xs text-stone-500 italic">Loading comparison…</div>
                  )}
                  {compare && compare.available && (
                    <>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-stone-500 uppercase text-[10px]">
                            <th className="text-left font-semibold py-1">Fuel</th>
                            <th className="text-right font-semibold py-1">{$}/kWh eff.</th>
                            <th className="text-right font-semibold py-1">Annual cost</th>
                            <th className="text-right font-semibold py-1">vs current</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compare.rows.map((r) => (
                            <tr
                              key={r.fuel_key}
                              className={`border-t border-stone-100 ${r.is_current ? 'bg-purple-50' : ''}`}
                              data-testid={`passport-compare-row-${r.fuel_key}`}
                            >
                              <td className="py-1.5 text-stone-900">
                                {r.fuel_label.split(' (')[0]}
                                {r.is_current && <span className="ml-1 text-[10px] text-purple-700 font-semibold">· this home</span>}
                              </td>
                              <td className="py-1.5 text-right font-mono text-stone-700">{$}{r.effective_unit_eur.toFixed(4)}</td>
                              <td className="py-1.5 text-right font-semibold text-stone-900">{$}{r.annual_cost_eur.toLocaleString()}</td>
                              <td className={`py-1.5 text-right font-semibold ${r.is_current ? 'text-stone-400' : r.delta_eur < 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {r.is_current ? '—' : (r.delta_eur > 0 ? '+' : '') + $ + r.delta_eur.toLocaleString() + '/yr'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Switch to heat-pump CTA */}
                      {compare.switch_to_heat_pump && (
                        <div
                          className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-xs"
                          data-testid="passport-switch-hp-cta"
                        >
                          <div className="font-semibold text-emerald-800 mb-1">
                            Switch to a heat pump: save {$}{compare.switch_to_heat_pump.annual_saving_eur.toLocaleString()}/yr
                          </div>
                          <div className="text-stone-700">
                            Install {$}{compare.switch_to_heat_pump.install_eur.toLocaleString()} − {data.country_label === 'UK' ? 'BUS' : 'SEAI'} grant {$}{compare.switch_to_heat_pump.seai_grant_eur.toLocaleString()}
                            = your cost <strong>{$}{compare.switch_to_heat_pump.net_cost_eur.toLocaleString()}</strong>.
                            Payback <strong>{compare.switch_to_heat_pump.payback_years} yrs</strong>.
                          </div>
                        </div>
                      )}

                      <div className="text-[10px] text-stone-400 mt-2">
                        Every fuel costed against the same {energyLabel} + floor area for this listing.
                      </div>
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => window.open(`${API}/api/listings/${propertyId}/heating-compare-pdf`, '_blank', 'noopener')}
                          data-testid="passport-compare-pdf"
                          className="text-xs font-semibold text-purple-700 hover:underline"
                        >
                          ⤓ Save comparison as PDF
                        </button>
                      </div>
                    </>
                  )}
                  {compare && !compare.available && (
                    <div className="text-xs text-stone-500">Comparison unavailable — {compare.reason || 'missing data'}.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-500" data-testid="passport-heating-empty">
          {energyLabel} or floor area missing — can&apos;t estimate heating cost.
        </div>
      )}

      {/* Retrofit panel */}
      {retro && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4" data-testid="passport-retrofit-panel">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">Retrofit to {retro.target_ber}</span>
            <Badge variant="outline" className="text-[10px] py-0 text-purple-700 border-purple-300">
              ROI {retro.payback_years} yrs
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Gross cost</div>
              <div className="font-bold text-stone-900">{fmt(retro.gross_cost_eur)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">{data.country_label === 'UK' ? 'UK' : 'SEAI'} grants</div>
              <div className="font-bold text-emerald-700">−{fmt(retro.grants_eur)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Your cost</div>
              <div className="font-bold text-stone-900">{fmt(retro.net_cost_eur)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Saves per year</div>
              <div className="font-bold text-purple-700">{fmt(retro.annual_saving_eur)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Solar + EV charger row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {solar && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="passport-solar-panel">
            <div className="flex items-center gap-2 mb-2">
              <Sun className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-stone-900">Solar PV — {solar.size_kwp} kWp</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>Install · <span className="font-medium">{fmt(solar.install_eur)}</span></div>
              <div>{data.country_label === 'UK' ? 'Grant' : 'SEAI grant'} · <span className="font-medium text-emerald-700">−{fmt(solar.seai_grant_eur)}</span></div>
              <div>Net cost · <span className="font-medium">{fmt(solar.net_cost_eur)}</span></div>
              <div>Saves / yr · <span className="font-medium text-purple-700">{fmt(solar.annual_benefit_eur)}</span></div>
              <div>Payback · <span className="font-medium">{solar.payback_years} yrs</span></div>
              <div>20-yr saving · <span className="font-medium text-purple-700">{fmt(solar.twenty_year_saving_eur)}</span></div>
            </div>
          </div>
        )}

        {ev && (
          <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-ev-panel">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-purple-700" />
              <span className="font-semibold text-stone-900">EV home-charger</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>Install · <span className="font-medium">{fmt(ev.domestic_install_eur)}</span></div>
              <div>{data.country_label === 'UK' ? 'Grant' : 'SEAI grant'} · <span className="font-medium text-emerald-700">−{fmt(ev.seai_grant_eur)}</span></div>
              <div>Your cost · <span className="font-medium">{fmt(ev.net_cost_eur)}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeEnergySection;
